import { Card, Flex, StatusIllustration, Tag, type StatusIllustrationProps } from "@wayflyer/flyui";
import { useIntl, type IntlShape } from "react-intl";

import { MESSAGES } from "./message-definitions";

export type ApplicationStatusCardState = "no-application" | "in-review" | "action-required";

export type ApplicationStatusCardProps =
  | { state: "no-application"; onApply: () => void; isApplying: boolean }
  | { state: "in-review" }
  | { state: "action-required" };

const ILLUSTRATION_STATUS: Record<ApplicationStatusCardState, NonNullable<StatusIllustrationProps["status"]>> = {
  "no-application": "info",
  "in-review": "info-pending",
  "action-required": "warning",
};

const getAdornment = (intl: IntlShape, state: ApplicationStatusCardState) => {
  switch (state) {
    case "action-required":
      return <Tag color="warning" content={intl.formatMessage(MESSAGES.tagActionRequired)} />;
    case "in-review":
      return <Tag color="info" content={intl.formatMessage(MESSAGES.tagInReview)} />;
    case "no-application":
      return undefined;
  }
};

const getFooter = (intl: IntlShape, state: ApplicationStatusCardState): string => {
  switch (state) {
    case "action-required":
      return intl.formatMessage(MESSAGES.footerActionRequired);
    case "in-review":
      return intl.formatMessage(MESSAGES.footerInReview);
    case "no-application":
      return intl.formatMessage(MESSAGES.footerNoApplication);
  }
};

const getActions = (intl: IntlShape, props: ApplicationStatusCardProps) => {
  switch (props.state) {
    case "no-application":
      return (
        <Card.Action
          action={{
            content: intl.formatMessage(MESSAGES.applyCta),
            onAction: props.onApply,
            loading: props.isApplying,
          }}
        />
      );
    case "in-review":
    case "action-required":
      return undefined;
  }
};

export const ApplicationStatusCard = (props: ApplicationStatusCardProps) => {
  const intl = useIntl();

  return (
    <Card
      slots={{
        header: (
          <Card.Header title={intl.formatMessage(MESSAGES.cardTitle)} adornment={getAdornment(intl, props.state)} />
        ),
        footer: getFooter(intl, props.state),
        actions: getActions(intl, props),
      }}
    >
      <Flex justify="center" align="center">
        <StatusIllustration status={ILLUSTRATION_STATUS[props.state]} />
      </Flex>
    </Card>
  );
};
